import React, { useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Heading,
  Message,
  RadioGroup,
  Text,
  TimeRangeInputValue,
  toast,
} from '@workwhile/ui'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEditShiftWorkFlowContextValue } from '../context/EditShiftWorkFlowProvider'
import { editShiftWorkFlowSchema } from '../schemas/editShiftWorkFlowSchema'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { WorkStatus } from 'api/typings'
import { formatDuration } from 'date-fns'
import { getWorkingHours } from 'lib/time'
import { formatCurrency } from 'lib/currency'
import { ClockedTimeField } from './ClockedTimeField'

const formatDateTimeInTimeZone = (
  time: string | Date | null | undefined,
  timezone: string
) => {
  if (!time) {
    return ''
  }
  return formatInTimeZone(time, timezone, "yyyy-MM-dd'T'HH:mm")
}

const workStatusOptions = Object.values(WorkStatus).map((status) => ({
  value: status,
  label: status,
}))

const parseTimeRangeValueToUTCDate = (
  time: TimeRangeInputValue,
  timezone: string
): Date | undefined => {
  // Standardizes TimeRangeInputValue to a Date object in UTC
  if (time instanceof Date) {
    return fromZonedTime(time, timezone)
  }
  if (typeof time !== 'string' || !time.trim()) return undefined

  const [datePart, timePart] = time.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hours, minutes] = (timePart ?? '00:00').split(':').map(Number)
  const inputDate = new Date(year, month - 1, day, hours, minutes) // Date constructor uses 0-indexed months

  return fromZonedTime(inputDate, timezone)
}

const parseTimeRangeTupleToUTCDate = (
  clockedInAt: TimeRangeInputValue,
  clockedOutAt: TimeRangeInputValue,
  timezone: string
): [string | undefined, string | undefined] => {
  // values returned by TimeRangeInput are 24hr time strings (e.g. 19:00) in shift TZ.
  // To save to data state, we need to:
  // - convert to a datetime
  // - convert from shifttz to UTC

  const utcClockedInAt = clockedInAt
    ? parseTimeRangeValueToUTCDate(clockedInAt, timezone)
    : undefined

  const utcClockedOutAt = clockedOutAt
    ? parseTimeRangeValueToUTCDate(clockedOutAt, timezone)
    : undefined

  const formattedutcClockedInAt = utcClockedInAt
    ? utcClockedInAt.toISOString()
    : undefined
  const formattedutcClockedOutAt = utcClockedOutAt
    ? utcClockedOutAt.toISOString()
    : undefined

  return [formattedutcClockedInAt, formattedutcClockedOutAt]
}

export const EditShiftWorkFlowMain = () => {
  const {
    state: {
      formData,
      workId,
      shiftId,
      payRate,
      payLumpSum,
      shiftStartsAt,
      shiftEndsAt,
      shiftBonuses,
      lunchBreak,
      worker,
      timezone,
      mutationState: { isPending, errorMessage: mutationErrorMessage },
    },
    actions: { submitFormToServer, setFormData, close },
  } = useEditShiftWorkFlowContextValue()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(editShiftWorkFlowSchema),
    defaultValues: {
      status: formData.status,
      clockedInAtAndClockedOutAt: [
        formData.clockedInAtAndClockedOutAt?.[0],
        formData.clockedInAtAndClockedOutAt?.[1],
      ],
      leftEarly: formData.leftEarly,
      existingValues: {
        status: formData.status,
        clockedInAtAndClockedOutAt: [
          formData.clockedInAtAndClockedOutAt?.[0],
          formData.clockedInAtAndClockedOutAt?.[1],
        ],
        leftEarly: formData.leftEarly,
      },
      bypassMaxDaysForEditWork: formData.bypassMaxDaysForEditWork,
    },
  })

  const watchedClockTimes = useWatch({
    control,
    name: 'clockedInAtAndClockedOutAt',
  })

  const { calculatedHours, lunchBreakMinutes, basePay } = useMemo(() => {
    const [clockedInAt, clockedOutAt] = watchedClockTimes ?? []
    if (!clockedInAt || !clockedOutAt) {
      return {
        calculatedHours: null,
        lunchBreakMinutes: 0,
        basePay: null,
      }
    }

    const calculatedHours = getWorkingHours(
      clockedInAt,
      clockedOutAt,
      lunchBreak
    )
    const unpaidBreakMinutes =
      lunchBreak?.type === 'unpaid' ? lunchBreak.durationInMinutes : 0

    const hours = Math.max(calculatedHours, 0)
    const pay = payRate ? payRate * hours : payLumpSum ?? 0

    return {
      calculatedHours: hours,
      lunchBreakMinutes: unpaidBreakMinutes,
      basePay: pay,
    }
  }, [watchedClockTimes, lunchBreak, payRate, payLumpSum])

  const errorMessage =
    errors.existingValues?.message?.toString() ||
    errors.clockedInAtAndClockedOutAt?.message?.toString() ||
    mutationErrorMessage

  const bonusTotal =
    shiftBonuses?.reduce((acc, bonus) => acc + bonus.amount, 0) ?? 0

  const onSubmit = handleSubmit((data) => {
    // FIXME: This is a hack to get around the `strictNullChecks` in our tsconfig.json which prevents correct type inference
    setFormData(data as unknown as any)

    const changedFields = Object.keys(data.existingValues).filter(
      (key): key is keyof typeof data.existingValues =>
        JSON.stringify(data[key as keyof typeof data]) !==
        JSON.stringify(data.existingValues[key])
    )

    // It is imporant that we only pass the changed fields to the server.
    // As server makes assumptions about the fields that are present in the request body
    const changedFieldsObject = changedFields.reduce(
      (acc, key) => {
        return {
          ...acc,
          [key]: data[key as keyof typeof data],
        }
      },
      {} as Partial<typeof data>
    )

    submitFormToServer(
      {
        workId,
        status: changedFieldsObject.status,
        clockedInAt: changedFieldsObject.clockedInAtAndClockedOutAt?.[0],
        clockedOutAt: changedFieldsObject.clockedInAtAndClockedOutAt?.[1],
        leftEarly: changedFieldsObject.leftEarly,
        bypassMaxDaysForEditWork: data.bypassMaxDaysForEditWork,
      },
      {
        onSuccess: () => {
          toast.success(
            `Updated ${worker.name}'s shift details successfully!`,
            {
              position: 'top-center',
            }
          )
          setFormData({})
          reset()
          close()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      // 'open' is controlled by Parent component so we set it to true at all times. Instead we just unmount the component when it's closed
      open={true}
      onClose={close}
      position="center"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit Shift Work
        </Heading>
        <Text mt={3} fontSize={2}>
          Worker
        </Text>
        <Text fontSize={2} color="lightText">
          {worker.name}
        </Text>
        <Flex mt={4} mb={4} alignItems="center">
          <Box width="50%">
            <Text fontSize={2}>Shift</Text>
            <Text fontSize={2} color="lightText">
              {shiftId}
            </Text>
          </Box>
          <Box width="50%">
            <Text fontSize={2}>Work ID</Text>
            <Text fontSize={2} color="lightText">
              {workId}
            </Text>
          </Box>
        </Flex>
        <Controller
          name="status"
          control={control}
          render={({ field, formState: { errors } }) => (
            <Field label="Status" error={errors.status?.message?.toString()}>
              <ModalSelect
                {...field}
                value={workStatusOptions.find(
                  (option) => option.value === field.value
                )}
                options={workStatusOptions}
                onChange={(value: any) => {
                  field.onChange(value.value)
                }}
              />
            </Field>
          )}
        />
        <Flex mt={4} mb={4} alignItems="center">
          <Box width="50%">
            <Text fontSize={2}>Shift Start</Text>
            <Text fontSize={2} color="lightText">
              {formatInTimeZone(
                shiftStartsAt,
                timezone,
                'yyyy-MM-dd, hh:mm a zzz'
              )}
            </Text>
          </Box>
          <Box width="50%">
            <Text fontSize={2}>Shift End</Text>
            <Text fontSize={2} color="lightText">
              {formatInTimeZone(
                shiftEndsAt,
                timezone,
                'yyyy-MM-dd, hh:mm a zzz'
              )}
            </Text>
          </Box>
        </Flex>
        <Controller
          name="clockedInAtAndClockedOutAt"
          control={control}
          render={({ field }) => {
            const [clockedInAt, clockedOutAt] = field.value
            const zonedClockedInAt = formatDateTimeInTimeZone(
              clockedInAt,
              timezone
            )
            const zonedClockedOutAt = formatDateTimeInTimeZone(
              clockedOutAt,
              timezone
            )
            return (
              <Flex mt={4} gap={4} flexWrap="wrap">
                <ClockedTimeField
                  label="Clocked In"
                  value={zonedClockedInAt}
                  onChange={(newValue) => {
                    field.onChange(
                      parseTimeRangeTupleToUTCDate(
                        newValue,
                        zonedClockedOutAt,
                        timezone
                      )
                    )
                  }}
                />
                <ClockedTimeField
                  label="Clocked Out"
                  value={zonedClockedOutAt}
                  onChange={(newValue) => {
                    field.onChange(
                      parseTimeRangeTupleToUTCDate(
                        zonedClockedInAt,
                        newValue,
                        timezone
                      )
                    )
                  }}
                />
              </Flex>
            )
          }}
        />

        {calculatedHours !== null ? (
          <Box>
            <Text fontSize={2}>Calculated Hours</Text>
            <Text fontSize={2} color="lightText">
              {formatDuration({ hours: calculatedHours }, { zero: true })}
              {lunchBreakMinutes > 0
                ? ` (${lunchBreakMinutes} min unpaid break)`
                : ''}
            </Text>
          </Box>
        ) : null}

        {basePay !== null ? (
          <Box mt={2}>
            <Text fontSize={2}>Estimated Pay</Text>
            <Text fontSize={2} color="lightText">
              {formatCurrency(basePay)}
              {payLumpSum ? ' lump sum' : ''}
              {bonusTotal > 0 ? ` + ${formatCurrency(bonusTotal)} bonus` : ''}
            </Text>
          </Box>
        ) : null}

        <Controller
          name="leftEarly"
          control={control}
          render={({ field }) => {
            return (
              <Field
                label="Left Early"
                error={errors.leftEarly?.message?.toString()}
                // Intentionally disabled. This should work, unsure why it's throwing an error
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                mt={4}
                width="fit-content"
              >
                <RadioGroup
                  {...field}
                  // Intentionally disabled. This should work, unsure why it's throwing an error
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  flexDirection={['column', 'row']}
                  gap={5}
                  options={[
                    {
                      value: true,
                      label: 'Yes',
                    },
                    {
                      value: false,
                      label: 'No',
                    },
                  ]}
                />
              </Field>
            )
          }}
        />

        <Controller
          name="bypassMaxDaysForEditWork"
          control={control}
          render={({ field }) => {
            return (
              <Field
                error={errors.bypassMaxDaysForEditWork?.message?.toString()}
                // Intentionally disabled. This should work, unsure why it's throwing an error
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                mt={4}
                width="fit-content"
              >
                <Checkbox {...field}>
                  <Text>Bypass limits on editing work</Text>
                </Checkbox>
              </Field>
            )
          }}
        />

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
